var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as _ from 'lodash';
import callable from './callable';
import { felaRenderer, felaRtlRenderer } from './felaRenderer';
import toCompactArray from './toCompactArray';
// ----------------------------------------
// Component level merge functions
// ----------------------------------------
/**
 * Merges a single component's styles (keyed by component part) with another component's styles.
 */
export var mergeComponentStyles = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    var initial = _.mapValues(target, function (partStyle) {
        return callable(partStyle);
    });
    return sources.reduce(function (partStylesPrepared, stylesByPart) {
        _.forEach(stylesByPart, function (partStyle, partName) {
            // Break references to avoid an infinite loop.
            // We are replacing functions with a new ones that calls the originals.
            var originalTarget = partStylesPrepared[partName];
            var originalSource = partStyle;
            partStylesPrepared[partName] = function (styleParam) {
                return _.merge(callable(originalTarget)(styleParam), callable(originalSource)(styleParam));
            };
        });
        return partStylesPrepared;
    }, initial);
};
/**
 * Merges a single component's variables with another component's variables.
 */
export var mergeComponentVariables = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    var initial = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return callable(target).apply(void 0, args) || {};
    };
    return sources.reduce(function (acc, next) {
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var accumulatedVariables = acc.apply(void 0, args);
            var computedComponentVariables = callable(next).apply(void 0, args);
            var mergedVariables = {};
            _.forEach(computedComponentVariables, function (variableToMerge, variableName) {
                var accumulatedVariable = accumulatedVariables[variableName];
                mergedVariables[variableName] =
                    _.isObject(variableToMerge) && _.isObject(accumulatedVariable)
                        ? __assign({}, accumulatedVariable, variableToMerge) : variableToMerge;
            });
            return __assign({}, accumulatedVariables, mergedVariables);
        };
    }, initial);
};
// ----------------------------------------
// Theme level merge functions
// ----------------------------------------
/**
 * Site variables can safely be merged at each Provider in the tree.
 * They are flat objects and do not depend on render-time values, such as props.
 */
export var mergeSiteVariables = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    var initial = __assign({}, target, { fontSizes: (target && target.fontSizes) || {} });
    return sources.reduce(function (acc, next) { return (__assign({}, acc, next)); }, initial);
};
/**
 * Component variables can be objects, functions, or an array of these.
 * The functions must be called with the final result of siteVariables, otherwise
 *   the component variable objects would have no ability to apply siteVariables.
 * Therefore, componentVariables must be resolved by the component at render time.
 * We instead pass down call stack of component variable functions to be resolved later.
 */
export var mergeThemeVariables = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    var displayNames = _.union.apply(_, [_.keys(target)].concat(_.map(sources, _.keys)));
    return sources.reduce(function (acc, next) {
        return displayNames.reduce(function (componentVariables, displayName) {
            if (!next)
                return acc;
            // Break references to avoid an infinite loop.
            // We are replacing functions with new ones that calls the originals.
            var originalTarget = acc[displayName];
            var originalSource = next[displayName];
            componentVariables[displayName] = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return __assign({}, callable(originalTarget).apply(void 0, args), callable(originalSource).apply(void 0, args));
            };
            return componentVariables;
        }, {});
    }, target);
};
/**
 * See mergeThemeVariables() description.
 * Component styles adhere to the same pattern as component variables, except
 *   that they return style objects.
 */
export var mergeThemeStyles = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    var initial = _.mapValues(target, function (stylesByPart) {
        return _.mapValues(stylesByPart, callable);
    });
    return sources.reduce(function (themeComponentStyles, next) {
        _.forEach(next, function (stylesByPart, displayName) {
            themeComponentStyles[displayName] = mergeComponentStyles(themeComponentStyles[displayName], stylesByPart);
        });
        return themeComponentStyles;
    }, initial);
};
export var mergeRTL = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    return !!sources.reduce(function (acc, next) {
        return typeof next === 'boolean' ? next : acc;
    }, target);
};
export var mergeFontFaces = function () {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    return toCompactArray.apply(void 0, sources);
};
export var mergeStaticStyles = function () {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    return toCompactArray.apply(void 0, sources);
};
export var mergeIcons = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    return Object.assign.apply(Object, [target].concat(sources));
};
export var mergeAnimations = function (target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    return Object.assign.apply(Object, [target].concat(sources));
};
export var mergeStyles = function () {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return sources.reduce(function (acc, next) {
            return _.merge(acc, callable(next).apply(void 0, args));
        }, {});
    };
};
var mergeThemes = function () {
    var themes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        themes[_i] = arguments[_i];
    }
    var emptyTheme = {
        siteVariables: {},
        componentVariables: {},
        componentStyles: {},
        fontFaces: [],
        staticStyles: [],
        icons: {},
        animations: {},
    };
    return themes.reduce(function (acc, next) {
        if (!next)
            return acc;
        acc.siteVariables = mergeSiteVariables(acc.siteVariables, next.siteVariables);
        acc.componentVariables = mergeThemeVariables(acc.componentVariables, next.componentVariables);
        acc.componentStyles = mergeThemeStyles(acc.componentStyles, next.componentStyles);
        // Merge icons set, last one wins in case of collisions
        acc.icons = mergeIcons(acc.icons, next.icons);
        // Latest RTL value wins
        acc.rtl = mergeRTL(acc.rtl, next.rtl);
        // Use the correct renderer for RTL
        acc.renderer = acc.rtl ? felaRtlRenderer : felaRenderer;
        acc.fontFaces = mergeFontFaces.apply(void 0, acc.fontFaces.concat(next.fontFaces));
        acc.staticStyles = mergeStaticStyles.apply(void 0, acc.staticStyles.concat(next.staticStyles));
        acc.animations = mergeAnimations(acc.animations, next.animations);
        return acc;
    }, emptyTheme);
};
export default mergeThemes;
